import { api, generateQueryParamFromObj, prefix } from "@/api";
import { layerType } from "@/types/api";
import { attachmentCategoryType, attachmentType } from "@/types/attachmnet";
import { childrenType, garaProjectType, projectPillarType, projectStatsType, projectType } from "@/types/project";
import { salType } from "@/types/sal";
import { AxiosHeaders, type AxiosRequestConfig } from "axios";
import { serialize } from "object-to-formdata";

const namespace = "project";

export const getList = (params: any, config: AxiosRequestConfig = {}) => {
  const query = generateQueryParamFromObj(params);
  return api<layerType<projectType>>({
    url: `${prefix}/happyway/${namespace}s?${query}`,
    method: "GET",
    ...config,
  });
};

export const getStats = (config: AxiosRequestConfig = {}) => {
  return api<projectStatsType>({
    url: `${prefix}/happyway/${namespace}s/stats`,
    method: "GET",
    ...config,
  });
};

export const deleteProject = (id: string | number, config: AxiosRequestConfig = {}) => {
  return api<any>({
    url: `${prefix}/${namespace}/project/${id}`,
    method: "DELETE",
    ...config,
  });
};

export const updateProject = (
  id: string | number,
  params: object,
  config: AxiosRequestConfig = {}
) => {
  return api<any>({
    url: `${prefix}/happyway/${namespace}s/${id}`,
    method: "PUT",
    data: params,
    ...config,
  });
};

export const getDetail = (id: number, params: any, config: AxiosRequestConfig = {}) => {
  const query = generateQueryParamFromObj(params);
  return api<{ data: projectType }>({
    url: `${prefix}/happyway/${namespace}s/${id}?${query}`,
    method: "GET",
    ...config,
  });
};

export const getDetailsFromPillar = (id: number, params: any, config: AxiosRequestConfig = {}) => {
  const query = generateQueryParamFromObj(params);
  return api<projectPillarType>({
    url: `${prefix}/project/${namespace}/${id}?${query}`,
    method: "GET",
    ...config,
  });
};

export const getProjectFilesFromPlatform = (
  id: number,
  params?: any,
  config: AxiosRequestConfig = {}
) => {
  const query = generateQueryParamFromObj(params);
  return api<attachmentCategoryType[]>({
    url: `${prefix}/platform/${namespace}/project/${id}/attachments?${query}`,
    method: "GET",
    ...config,
  });
};

export const deleteRoleOnProject = (
  id: string | number,
  roleId: string | number,
  config: AxiosRequestConfig = {}
) => {
  return api<any>({
    url: `${prefix}/${namespace}/project/${id}/role/${roleId}`,
    method: "DELETE",
    ...config,
  });
};

export const deleteRoleOnProjectLayer = (
  id: string | number,
  roleId: string | number,
  config: AxiosRequestConfig = {}
) => {
  return api<any>({
    url: `${prefix}/happyway/${namespace}s/${id}/role/${roleId}`,
    method: "DELETE",
    ...config,
  });
};

export const assignCoachOnProject = (
  id: string | number,
  params: object,
  config: AxiosRequestConfig = {}
) => {
  return api<any>({
    url: `${prefix}/happyway/${namespace}/${id}/assign-coach`,
    method: "POST",
    data: params,
    ...config,
  });
};

export const assignTutorOnProject = (
  id: string | number,
  params: object,
  config: AxiosRequestConfig = {}
) => {
  return api<any>({
    url: `${prefix}/happyway/${namespace}s/${id}/tutor`,
    method: "POST",
    data: params,
    ...config,
  });
};

export const createOpportunity = (params: object, config: AxiosRequestConfig = {}) => {
  return api<any>({
    url: `${prefix}/happyway/projects/opportunity`,
    method: "POST",
    data: params,
    ...config,
  });
};

export const createMetricComputation = (params: object, config: AxiosRequestConfig = {}) => {
  return api<any>({
    url: `${prefix}/happyway/${namespace}s/metric-computation`,
    method: "POST",
    data: params,
    ...config,
  });
};

export const createMetricComputationProjects = (
  id: number,
  params: object,
  config: AxiosRequestConfig = {}
) => {
  return api<any>({
    url: `${prefix}/happyway/${namespace}s/metriccomputation/projects/${id}`,
    method: "POST",
    data: params,
    ...config,
  });
};

export const createGestioneCantiereProjects = (
  params: object,
  config: AxiosRequestConfig = {}
) => {
  return api<any>({
    url: `${prefix}/happyway/${namespace}s/cantiere`,
    method: "POST",
    data: params,
    ...config,
  });
};

export const createGestioneCantierePrivateProjects = (
  params: object,
  config: AxiosRequestConfig = {}
) => {
  return api<any>({
    url: `${prefix}/happyway/${namespace}s/cantiereprivate`,
    method: "POST",
    data: params,
    ...config,
  });
};

export const getMetricComputationProjects = (id: number, config: AxiosRequestConfig = {}) => {
  return api<childrenType[]>({
    url: `${prefix}/happyway/${namespace}s/metriccomputation/projects?project_id=${id}`,
    method: "GET",
    ...config,
  });
};

export const uploadFileOnProject = (params: object) => {
  const headers = new AxiosHeaders({ "Content-Type": "multipart/form-data" });
  return api<attachmentType>({
    url: `${prefix}/platform/${namespace}/project/attachment/upload`,
    method: "POST",
    data: serialize(params),
    headers: headers,
  });
};

export const getSALList = (id: number, params?: any, config: AxiosRequestConfig = {}) => {
  const query = generateQueryParamFromObj(params);
  return api<Array<salType>>({
    url: `${prefix}/happyway/${namespace}s/cantiere/sal/${id}?${query}`,
    method: "GET",
    ...config,
  });
};

export const createSAL = (
  params: object, 
  config: AxiosRequestConfig = {}
) => {
  return api<any>({
    url: `${prefix}/happyway/projects/sal`,
    method: "POST",
    data: params,
    ...config,
  });
};

export const updateSAL = (
  id: string | number,
  params: object,
  config: AxiosRequestConfig = {}
) => {
  return api<any>({
    url: `${prefix}/happyway/${namespace}s/sal/${id}`,
    method: "PUT",
    data: params,
    ...config,
  });
};

export const getCantierePrivateList = (id: number, config: AxiosRequestConfig = {}) => {
  return api<childrenType[]>({
    url: `${prefix}/happyway/${namespace}s/agora/${id}/cantiereprivate/children`,
    method: "GET",
    ...config,
  });
};

export const loadGaraDetail = (id: number, config: AxiosRequestConfig = {}) => {
  return api<garaProjectType[]>({
    url: `${prefix}/happyway/${namespace}s/metriccomputation/estimative-tender/${id}`,
    method: "GET",
    ...config,
  });
};

